'use client';

/**
 * SingleRuleGiftChangeWidget
 *
 * Bottom sheet component for changing a gift within the same rule.
 * This is used when user clicks "Đổi quà" button on a cart gift item.
 *
 * Key features:
 * - Shows only products from the same rule (same ruleId)
 * - Allows user to select a different gift option
 * - Allows user to remove the current gift
 * - Updates the cart item reference logic with new selection
 * - Uses GiftsSelectorWidgetContents for consistent UI
 * - Restores session from cartItemReference if session was deleted
 * - Includes quantity stepper to change product quantity
 *
 * Uses real withAppBottomSheet HOC from mario-nextjs-react-ui-core
 * (responsive: Modal on desktop, BottomSheet on mobile viewport)
 */
import {
    withAppBottomSheet,
    type AppBottomSheetExtraParamsProps,
    type AppBottomSheetBackdropProps,
    type AppBottomSheetFooterProps,
} from '@kidslabs/mario-nextjs-react-ui-core';
import cssStyles from './single-rule-gift-change-widget.module.css';

import React, {
    useCallback,
    useImperativeHandle,
    useMemo,
    forwardRef,
    useRef,
    type ForwardRefRenderFunction,
    type ForwardedRef,
} from 'react';
import {
    type SingleRuleGiftChangeWidgetProps,
    type SingleRuleGiftChangeWidgetRef,
} from './single-rule-gift-change-widget.types';
import { GiftsSelectorWidgetContents } from '../gifts-selector-widget-contents/gifts-selector-widget-contents/gifts-selector-widget-contents';
import { GiftSelectorBottomSheetFooter } from '../gift-selector-bottom-sheet-footer/gift-selector-bottom-sheet-footer';
import {
    type CartItemReference,
    type GiftChangeParams,
} from '../quantity-change-gift-selector-widget/quantity-change-gift-selector-widget.types';
import { type FreeGiftsData } from '../../../../types/front.graphql';
import { giftSelectionStoreAPI } from '../../stores/simple-gift-selection-store.public-api';
import { useTranslation } from 'react-i18next';

// Memoized footer component
const MemoizedFooter = React.memo<{
    onBottomSheetClose: () => void;
    onConfirm: () => void;
}>(({ onBottomSheetClose, onConfirm }) => {
    return <GiftSelectorBottomSheetFooter onBottomSheetClose={onBottomSheetClose} onConfirm={onConfirm} />;
});

MemoizedFooter.displayName = 'MemoizedFooter';

const SingleRuleGiftChangeWidgetInner: ForwardRefRenderFunction<
    SingleRuleGiftChangeWidgetRef,
    SingleRuleGiftChangeWidgetProps
> = (props: SingleRuleGiftChangeWidgetProps, ref: ForwardedRef<SingleRuleGiftChangeWidgetRef>) => {
    // Only extract callbacks from props - data comes via openBottomSheet injection
    const { bottomSheet, onClose, onGiftChanged } = props;
    const { t } = useTranslation('flash-sale');
    // Store injected data locally for use in callbacks
    const injectedDataRef = useRef<{
        cartItemReference: CartItemReference;
        ruleId: number;
        availableGifts: FreeGiftsData[];
        sessionId: string;
    } | null>(null);

    const handleClose = useCallback(() => {
        console.log('[SingleRuleGiftChangeWidget] Closing without saving changes');
        bottomSheet?.close();
        onClose?.();
    }, [bottomSheet, onClose]);

    const handleConfirm = useCallback(() => {
        console.log('[SingleRuleGiftChangeWidget] Confirming gift selection');

        // Use injected data from ref
        const injectedData = injectedDataRef.current;
        if (!injectedData) {
            console.warn('[SingleRuleGiftChangeWidget] No injected data available');
            bottomSheet?.close();
            return;
        }

        const { sessionId, ruleId } = injectedData;

        // Get selected gifts from session and filter by ruleId
        const selectedGifts = giftSelectionStoreAPI.state.getSelectedGifts(sessionId);
        const giftForRule = selectedGifts.find(gift => gift.ruleId === ruleId);

        if (giftForRule) {
            onGiftChanged?.(giftForRule);
        }
        // No action needed when giftForRule is not found - selection was cleared

        bottomSheet?.close();
    }, [bottomSheet, onGiftChanged]);

    // Header configuration for bottom sheet
    const headerProps = useMemo(
        () => ({
            title: t('text_gift_choose_accept-change', 'Chọn quà tặng'),
            titleProps: {
                className: cssStyles.titleGiftChange,
            },
        }),
        [t]
    );

    const extraParams = useMemo<AppBottomSheetExtraParamsProps>(
        () => ({
            enableDynamicSizing: false,
            scrollEnabled: false,
            enableContentPanningGesture: false,
            snapPoints: ['85%', '85%'],
        }),
        []
    );

    const backdropProps = useMemo<AppBottomSheetBackdropProps>(
        () => ({
            pressBehavior: 'close',
        }),
        []
    );

    // Memoize footer separately
    const footerElement = useMemo<AppBottomSheetFooterProps>(
        () => ({ component: <MemoizedFooter onBottomSheetClose={handleClose} onConfirm={handleConfirm} /> }),
        [handleClose, handleConfirm]
    );

    /**
     * Open bottom sheet with injected GiftChangeParams
     * All data comes through params, not props
     * @param params - Contains cartItemReference, logic, and availableGiftsForRule
     */
    const openBottomSheet = useCallback(
        (params: GiftChangeParams) => {
            const {
                cartItemReference: injectedCartItemRef,
                logic,
                availableGiftsForRule: injectedAvailableGifts,
            } = params;
            const injectedRuleId = typeof logic.logicId === 'string' ? parseInt(logic.logicId, 10) : logic.logicId;

            if (!injectedRuleId) {
                console.warn('[SingleRuleGiftChangeWidget] No ruleId in injected logic');
                return;
            }

            // Extract product from injected cart item reference
            const firstCartItem = injectedCartItemRef.cartItem;
            if (!firstCartItem?.product) {
                console.warn('[SingleRuleGiftChangeWidget] No cart product in injected data');
                return;
            }
            const injectedCartProduct = firstCartItem.product;

            // Store injected data for use in callbacks
            injectedDataRef.current = {
                cartItemReference: injectedCartItemRef,
                ruleId: injectedRuleId,
                availableGifts: injectedAvailableGifts ? [injectedAvailableGifts] : [],
                sessionId: logic.sessionId ?? '',
            };

            // Build fresh filtered reference with INJECTED data
            const currentFilteredLogics =
                injectedCartItemRef.logics?.filter(l => {
                    const logicIdNum = typeof l.logicId === 'string' ? parseInt(l.logicId, 10) : l.logicId;
                    return logicIdNum === injectedRuleId;
                }) ?? [];

            // In replacement mode, use the rule's allocatedQty (quantity allocated to this
            // specific rule) instead of the full cart item qty. This ensures downstream
            // components (GiftItem, calculateGiftEligibility) compute eligibility based on
            // the quantity that actually triggers this rule, not the total product quantity.
            const matchedLogic = currentFilteredLogics[0];
            const currentQuantity = matchedLogic?.allocatedQty ?? injectedCartItemRef.qty ?? 1;

            const currentFilteredReference = {
                ...injectedCartItemRef,
                logics: currentFilteredLogics,
                qty: currentQuantity,
            };

            // Build content with INJECTED data
            const content = (
                <div className={cssStyles.container}>
                    <GiftsSelectorWidgetContents
                        product={injectedCartProduct}
                        productQuantity={currentQuantity}
                        cartItemReference={currentFilteredReference}
                        availableGiftsForRule={injectedAvailableGifts ? [injectedAvailableGifts] : []}
                        isReplacementMode={true}
                    />
                </div>
            );

            // Inject content into bottom sheet using real UI Core HOC
            bottomSheet?.inject(content, headerProps, extraParams, footerElement, backdropProps);
            bottomSheet?.show();

            console.log('[SingleRuleGiftChangeWidget] Bottom sheet shown');
        },
        [bottomSheet, headerProps, extraParams, footerElement, backdropProps]
    );

    useImperativeHandle(ref, () => ({ openBottomSheet }), [openBottomSheet]);

    return null;
};

SingleRuleGiftChangeWidgetInner.displayName = 'SingleRuleGiftChangeWidgetInner';

export const SingleRuleGiftChangeWidget = withAppBottomSheet(forwardRef(SingleRuleGiftChangeWidgetInner));
